
export default class MessageHelper {
    /**
     * 显示临时消息（自动消失）
     * @param {Phaser.Scene} scene - 场景对象
     * @param {string} message - 消息内容
     * @param {string} type - 消息类型: 'success', 'error', 'info', 'warning'
     * @param {number} duration - 显示时长（毫秒），默认2000
     * @param {Object} position - 位置 {x, y}，默认屏幕中央
     */
    static showToast(scene, message, type = 'info', duration = 2000, position = null) {
        if (!scene || !scene.add) return null;
        
        const { width, height, centerX, centerY } = scene.cameras.main;
        const pos = position || { x: centerX, y: centerY + 100 };
        
        // 根据类型选择颜色
        const colors = {
            success: '#00ff00',
            error: '#ff0000',
            info: '#4cc9f0',
            warning: '#ffaa00'
        };
        const color = colors[type] || colors.info;
        
        // 创建背景
        const bg = scene.add.graphics();
        bg.fillStyle(0x000000, 0.8);
        bg.fillRoundedRect(pos.x - 150, pos.y - 20, 300, 40, 5);
        bg.lineStyle(2, parseInt(color.replace('#', ''), 16), 1);
        bg.strokeRoundedRect(pos.x - 150, pos.y - 20, 300, 40, 5);
        bg.setScrollFactor(0);
        bg.setDepth(10000);
        
        // 创建文本
        const text = scene.add.text(
            pos.x, pos.y,
            message,
            {
                fontSize: '16px',
                fill: color,
                fontFamily: 'Arial',
                align: 'center',
                wordWrap: { width: 280 }
            }
        );
        text.setOrigin(0.5);
        text.setScrollFactor(0);
        text.setDepth(10001);
        
        // 自动消失
        scene.time.delayedCall(duration, () => {
            bg.destroy();
            text.destroy();
        });
        
        return { bg, text };
    }
    
    /**
     * 显示成功消息
     */
    static showSuccess(scene, message, duration = 2000) {
        return this.showToast(scene, `✓ ${message}`, 'success', duration);
    }
    
    /**
     * 显示错误消息
     */
    static showError(scene, message, duration = 3000) {
        return this.showToast(scene, `✗ ${message}`, 'error', duration);
    }
    
    /**
     * 显示信息消息
     */
    static showInfo(scene, message, duration = 2000) {
        return this.showToast(scene, `ℹ ${message}`, 'info', duration);
    }
    
    /**
     * 显示警告消息
     */
    static showWarning(scene, message, duration = 2500) {
        return this.showToast(scene, `⚠ ${message}`, 'warning', duration);
    }
    
    /**
     * 显示操作反馈（带动画）
     * @param {Phaser.Scene} scene - 场景对象
     * @param {string} message - 消息内容
     * @param {string} type - 消息类型
     * @param {Object} position - 位置
     */
    static showFeedback(scene, message, type = 'info', position = null) {
        const toast = this.showToast(scene, message, type, 1500, position);
        
        if (toast && toast.text) {
            // 添加淡入动画
            toast.text.setAlpha(0);
            scene.tweens.add({
                targets: toast.text,
                alpha: 1,
                duration: 200,
                ease: 'Power2'
            });
            
            // 添加淡出动画
            scene.time.delayedCall(1300, () => {
                scene.tweens.add({
                    targets: [toast.text, toast.bg],
                    alpha: 0,
                    duration: 200,
                    ease: 'Power2',
                    onComplete: () => {
                        if (toast.bg) toast.bg.destroy();
                        if (toast.text) toast.text.destroy();
                    }
                });
            });
        }
        
        return toast;
    }
    
    /**
     * 显示场景名称提示（居中显示，自动消失）
     * @param {Phaser.Scene} scene - 场景对象
     * @param {string} message - 场景名称
     * @param {number} duration - 显示时长（毫秒），默认1000
     */
    static showSceneNotification(scene, message, duration = 1000) {
        if (!scene || !scene.add) return null;
        
        const { centerX, centerY } = scene.cameras.main;
        const posX = centerX;
        const posY = centerY - 80; // 屏幕中央稍微偏上
        
        // 创建半透明背景
        const bg = scene.add.graphics();
        bg.fillStyle(0x000000, 0.6);
        bg.fillRoundedRect(posX - 180, posY - 25, 360, 50, 8);
        bg.lineStyle(2, 0x4cc9f0, 0.8);
        bg.strokeRoundedRect(posX - 180, posY - 25, 360, 50, 8);
        bg.setScrollFactor(0);
        bg.setDepth(10000);
        bg.setAlpha(0); // 初始透明
        
        // 创建文本
        const text = scene.add.text(
            posX, posY,
            message,
            {
                fontSize: '24px',
                fill: '#ffffff',
                fontFamily: 'Arial',
                align: 'center',
                fontStyle: 'bold',
                wordWrap: { width: 340 }
            }
        );
        text.setOrigin(0.5);
        text.setScrollFactor(0);
        text.setDepth(10001);
        text.setAlpha(0); // 初始透明
        
        // 淡入动画
        scene.tweens.add({
            targets: [bg, text],
            alpha: 1,
            duration: 300,
            ease: 'Power2'
        });
        
        // 淡出并销毁
        scene.time.delayedCall(duration, () => {
            scene.tweens.add({
                targets: [bg, text],
                alpha: 0,
                duration: 300,
                ease: 'Power2',
                onComplete: () => {
                    if (bg) bg.destroy();
                    if (text) text.destroy();
                }
            });
        });
        
        return { bg, text };
    }
    
    /**
     * 显示确认对话框
     * @param {Phaser.Scene} scene - 场景对象
     * @param {string} message - 消息内容
     * @param {Function} onConfirm - 确认回调
     * @param {Function} onCancel - 取消回调（可选）
     */
    static showConfirm(scene, message, onConfirm, onCancel = null) {
        if (!scene || !scene.add) return null;
        
        const { width, height, centerX, centerY } = scene.cameras.main;
        
        // 设置标志，表示正在显示确认对话框，禁用空格键的交互功能
        scene.isConfirmDialogActive = true;
        
        // 临时禁用 WorldUI 的对话框处理，防止空格键触发
        let originalIsDialogActive = false;
        if (scene.worldUI) {
            originalIsDialogActive = scene.worldUI.isDialogActive;
            scene.worldUI.isDialogActive = false; // 临时禁用
        }
        
        // 创建遮罩
        const mask = scene.add.graphics();
        mask.fillStyle(0x000000, 0.7);
        mask.fillRect(0, 0, width, height);
        mask.setScrollFactor(0);
        mask.setDepth(9999);
        mask.setInteractive(new Phaser.Geom.Rectangle(0, 0, width, height), Phaser.Geom.Rectangle.Contains);
        
        // 创建对话框背景
        const dialogBg = scene.add.graphics();
        dialogBg.fillStyle(0x1a1a2e, 1);
        dialogBg.fillRoundedRect(centerX - 200, centerY - 80, 400, 160, 10);
        dialogBg.lineStyle(3, 0x4cc9f0, 1);
        dialogBg.strokeRoundedRect(centerX - 200, centerY - 80, 400, 160, 10);
        dialogBg.setScrollFactor(0);
        dialogBg.setDepth(10000);
        
        // 创建消息文本
        const messageText = scene.add.text(
            centerX, centerY - 30,
            message,
            {
                fontSize: '18px',
                fill: '#ffffff',
                fontFamily: 'Arial',
                align: 'center',
                wordWrap: { width: 360 }
            }
        );
        messageText.setOrigin(0.5);
        messageText.setScrollFactor(0);
        messageText.setDepth(10001);
        
        // 创建确认按钮
        const confirmBtn = scene.add.graphics();
        confirmBtn.fillStyle(0x00aa00, 1);
        confirmBtn.fillRoundedRect(centerX - 150, centerY + 40, 120, 35, 5);
        confirmBtn.lineStyle(2, 0x00ff00, 1);
        confirmBtn.strokeRoundedRect(centerX - 150, centerY + 40, 120, 35, 5);
        confirmBtn.setScrollFactor(0);
        confirmBtn.setDepth(10001);
        confirmBtn.setInteractive(new Phaser.Geom.Rectangle(centerX - 150, centerY + 40, 120, 35), Phaser.Geom.Rectangle.Contains);
        confirmBtn.input.cursor = 'pointer';
        
        const confirmText = scene.add.text(
            centerX - 90, centerY + 57,
            '确认',
            {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'Arial'
            }
        );
        confirmText.setOrigin(0.5);
        confirmText.setScrollFactor(0);
        confirmText.setDepth(10002);
        
        // 创建取消按钮
        const cancelBtn = scene.add.graphics();
        cancelBtn.fillStyle(0xaa0000, 1);
        cancelBtn.fillRoundedRect(centerX + 30, centerY + 40, 120, 35, 5);
        cancelBtn.lineStyle(2, 0xff0000, 1);
        cancelBtn.strokeRoundedRect(centerX + 30, centerY + 40, 120, 35, 5);
        cancelBtn.setScrollFactor(0);
        cancelBtn.setDepth(10001);
        cancelBtn.setInteractive(new Phaser.Geom.Rectangle(centerX + 30, centerY + 40, 120, 35), Phaser.Geom.Rectangle.Contains);
        cancelBtn.input.cursor = 'pointer';
        
        const cancelText = scene.add.text(
            centerX + 90, centerY + 57,
            '取消',
            {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'Arial'
            }
        );
        cancelText.setOrigin(0.5);
        cancelText.setScrollFactor(0);
        cancelText.setDepth(10002);
        
        // 关闭对话框
        const close = () => {
            // 清除确认对话框标志
            scene.isConfirmDialogActive = false;
            
            // 恢复 WorldUI 的对话框状态
            if (scene.worldUI) {
                scene.worldUI.isDialogActive = originalIsDialogActive;
            }
            
            mask.destroy();
            dialogBg.destroy();
            messageText.destroy();
            confirmBtn.destroy();
            confirmText.destroy();
            cancelBtn.destroy();
            cancelText.destroy();
        };
        
        // 按钮事件
        confirmBtn.on('pointerdown', () => {
            close();
            if (onConfirm) onConfirm();
        });
        
        cancelBtn.on('pointerdown', () => {
            close();
            if (onCancel) onCancel();
        });
        
        // ESC键关闭
        const escKey = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
        const escHandler = () => {
            close();
            if (onCancel) onCancel();
            escKey.off('down', escHandler);
        };
        escKey.on('down', escHandler);
        
        return { mask, dialogBg, messageText, confirmBtn, cancelBtn, close };
    }
}

